import { until } from "@vueuse/core";

import { throttle } from "lodash";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
    // discardDraftsAfterSync,
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import { registerFlush } from "@/composables/pending-flush";
import { Artefact } from "@/lib/artefacts";
import {
    getCoreValuesArtefact,
    getDescriptionArtefact,
    getMissionArtefact,
    getSpecializationsArtefact,
    getTargetMarketsArtefact,
    getVisionArtefact,
} from "@/lib/artefacts/markdown";
import { syncChannel } from "@/lib/broadcast-channels";
import { types, useClient, useServices } from "@/lib/openapi";
import { headerManager } from "@/lib/requests";
import { baseSuccessToastMessage } from "@/primevue/init";
import {
    EntityInfo,
    EntityType,
    useCurrentEntityManager,
} from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";
import { Stek, useSteksManager, useSteksStore } from "@/stores/steks-store";
import { useSyncStore } from "@/stores/sync-store";

export const useStekStore = defineStore("stek", () => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const { organization } = useOrganizationManager();
    const { steks, isLoading: isLoadingSteks } = useSteksManager();
    const { fetchSteks, fetchLogo: fetchLogoForStek } = useSteksStore();
    const isFetchingStek = ref(false);
    const logo = ref<Artefact>();
    const logoDataUrl = ref<string>("");
    const fqdn = ref<string>();
    const originalFqdn = ref<string>();
    const isFetchingLogo = ref(false);
    const isFetchingDescription = ref(false);
    const isFetchingMission = ref(false);
    const isFetchingVision = ref(false);
    const isFetchingCoreValues = ref(false);
    const isFetchingSpecializations = ref(false);
    const isFetchingTargetMarkets = ref(false);
    const selectionInProgress = ref(false);

    const controller = ref(new AbortController());
    const stek = ref<Stek | undefined>();
    const stekInfo = ref<EntityInfo | undefined>();
    // Access release store lazily to avoid circular dependency
    // (release-store uses useStekManager which triggers this store)
    const revisionQuery = computed(() => {
        const revision = useReleaseStore().activeRevision;
        return revision ? new URLSearchParams({ revision }) : undefined;
    });

    const entityId = computed(() => stek.value?.identity);
    const description = ref<Artefact<"text/markdown"> | undefined>();
    const mission = ref<Artefact<"text/markdown"> | undefined>();
    const vision = ref<Artefact<"text/markdown"> | undefined>();
    const coreValues = ref<Artefact<"text/markdown"> | undefined>();
    const specializations = ref<Artefact<"text/markdown"> | undefined>();
    const targetMarkets = ref<Artefact<"text/markdown"> | undefined>();

    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.STEK,
        attribute: "description",
        artefact: description,
    });

    const missionDraft = useDraftManager({
        entityId,
        entityType: EntityType.STEK,
        attribute: "mission",
        artefact: mission,
    });

    const visionDraft = useDraftManager({
        entityId,
        entityType: EntityType.STEK,
        attribute: "vision",
        artefact: vision,
    });

    const coreValuesDraft = useDraftManager({
        entityId,
        entityType: EntityType.STEK,
        attribute: "core_values",
        artefact: coreValues,
    });

    const specializationsDraft = useDraftManager({
        entityId,
        entityType: EntityType.STEK,
        attribute: "specializations",
        artefact: specializations,
    });

    const targetMarketsDraft = useDraftManager({
        entityId,
        entityType: EntityType.STEK,
        attribute: "target_markets",
        artefact: targetMarkets,
    });

    discardDraftsAfterSync(
        entityId,
        [
            descriptionDraft,
            missionDraft,
            visionDraft,
            coreValuesDraft,
            specializationsDraft,
            targetMarketsDraft,
        ],
        EntityType.STEK
    );

    onMounted(() => {
        const { stek: localStekInfo } = useCurrentEntityManager();
        watch(
            localStekInfo,
            (newStekInfo) => {
                stekInfo.value = newStekInfo;
            },
            { immediate: true }
        );
    });

    const isFetching = computed(() => {
        return (
            isFetchingStek.value ||
            isFetchingLogo.value ||
            isFetchingDescription.value ||
            isFetchingMission.value ||
            isFetchingVision.value ||
            isFetchingCoreValues.value ||
            isFetchingSpecializations.value ||
            isFetchingTargetMarkets.value
        );
    });

    const isModified = computed(() => {
        return Boolean(
            logo.value?.isModified ||
            fqdn.value !== originalFqdn.value ||
            description.value?.isModified ||
            mission.value?.isModified ||
            vision.value?.isModified ||
            coreValues.value?.isModified ||
            specializations.value?.isModified ||
            targetMarkets.value?.isModified
        );
    });

    const isStekReady = computed(() => {
        return Boolean(
            !isLoadingSteks.value &&
            !isFetching.value &&
            !selectionInProgress.value &&
            stek.value?.identity &&
            steks.value
        );
    });

    async function selectStekFromSteks() {
        if (!route.meta.requiresStek) {
            stek.value = undefined;
            return;
        }

        // When there is no selected organization, there is no way to
        // select the current stek.
        if (!route.params.orgName) {
            return;
        }

        // Ensure organization is available (steks depend on it)
        if (!organization.value?.identity) {
            return;
        }

        // Prevent concurrent selection attempts
        if (selectionInProgress.value) {
            return;
        }

        try {
            selectionInProgress.value = true;

            // Wait for steks list to be loaded and detail fetching to complete
            await until(
                () =>
                    !isLoadingSteks.value &&
                    !isFetching.value &&
                    steks.value !== undefined
            ).toBeTruthy();

            if (!steks.value) return;

            const stekName = route.params.stekName;
            if (
                stek.value &&
                (stek.value.name === stekName ||
                    stek.value.identity === stekName)
            ) {
                return;
            }

            // If there are no Steks redirect to the organization home page, unless
            // we're currently loading the steks.
            if (steks.value.length === 0) {
                router.replace({
                    name: "organizationHome",
                    params: {
                        orgName: route.params.orgName,
                    },
                });
                stek.value = undefined;
                return;
            }

            if (steks.value.length === 1) {
                stek.value = steks.value[0];
            } else {
                stek.value = steks.value.find(
                    (stek) =>
                        stek.name === stekName || stek.identity === stekName
                );
            }

            if (!stek.value) {
                router.replace({
                    name: "organizationHome",
                    params: {
                        orgName: organization.value!.name,
                    },
                });
                return;
            }

            // When stekName is undefined while a stek is selected, update
            // the url to include the selected stek. (this can happen when there
            // is only one stek, in which case the user won't be shown the stek
            // picker where clicking a stek updates the url.)
            if (
                !stekName &&
                organization.value &&
                route.meta.requiresStek &&
                !route.meta.preventRedirect
            ) {
                router.replace({
                    name: "stekPage",
                    params: {
                        orgName: organization.value.name,
                        stekName: stek.value.name,
                    },
                });
            }

            try {
                await fetchStek();
            } catch (error) {
                console.error("Failed to fetch stek:", error);
                // Continue showing the stek page even if logo or other details failed to load
                // The basic information is already available in stek.value
                toast.add({
                    severity: "warn",
                    summary: "Some Stek details could not be loaded",
                    detail: "Using cached information instead",
                    life: 5000,
                });
            }
        } finally {
            selectionInProgress.value = false;
        }
    }

    /**
     * Deletes the current Stek.
     */
    async function remove(opts: { showToast: boolean } = { showToast: true }) {
        if (!stek.value) {
            console.warn("Tried to delete stek, but no stek active.");
            return;
        }
        if (!organization.value) {
            console.warn("Tried to delete stek, but no organization active.");
            return;
        }

        const api = useServices({
            requires: { auth: true, organization: true, stek: true },
        });
        const { response } = await api.startStekStekMgtDeleteStekStartPost({});

        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Stek ${stek.value.name} deleted`,
                });
            }
            await fetchSteks();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not delete " + stek.value.name,
                    detail: response.status,
                });
            }
        }
    }

    const queueSave = async () => {
        if (!isModified.value || !stek.value) {
            return;
        }

        // Queue content changes (accessible to USER role and above) through the sync store.
        const updateContent: types.StartStekStekMgtUpdateStekStartPostData = {
            body: {
                stek_id: stek.value.identity,
            },
            url: "/stek/stek_mgt/update_stek/start",
        };
        if (logo.value?.isModified) {
            updateContent.body!.logo = logo.value.blob;
        }

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (mission.value?.isModified) {
            updateContent.body.mission = mission.value.blob;
        }

        if (vision.value?.isModified) {
            updateContent.body.vision = vision.value.blob;
        }

        if (coreValues.value?.isModified) {
            updateContent.body.core_values = coreValues.value.blob;
        }

        if (specializations.value?.isModified) {
            updateContent.body.specializations = specializations.value.blob;
        }

        if (targetMarkets.value?.isModified) {
            updateContent.body.target_markets = targetMarkets.value.blob;
        }

        if (fqdn.value !== originalFqdn.value) {
            updateContent.body.fqdn = fqdn.value;
        }

        if (Object.keys(updateContent.body).length > 1) {
            useSyncStore().queueSync({
                entityType: EntityType.STEK,
                args: [updateContent],
            });

            if (logo.value?.isModified) {
                logo.value.persist();
            }
            if (description.value?.isModified) {
                description.value.persist();
            }
            if (mission.value?.isModified) {
                mission.value.persist();
            }
            if (vision.value?.isModified) {
                vision.value.persist();
            }
            if (coreValues.value?.isModified) {
                coreValues.value.persist();
            }
            if (specializations.value?.isModified) {
                specializations.value.persist();
            }
            if (targetMarkets.value?.isModified) {
                targetMarkets.value.persist();
            }
            if (fqdn.value !== originalFqdn.value) {
                originalFqdn.value = fqdn.value;
            }
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        await mission.value?.resetContent();
        await vision.value?.resetContent();
        await coreValues.value?.resetContent();
        await specializations.value?.resetContent();
        await targetMarkets.value?.resetContent();
        fqdn.value = originalFqdn.value;
    };

    async function exportStek() {
        if (!stek.value) return;

        try {
            const client = useClient({
                requires: { auth: true, organization: true, stek: true },
            });
            // Use client.request directly for robustness
            const { response, data } = await client.request({
                method: "POST",
                url: "/stek/stek_mgt/export_stek/start",
            });

            if (!response.ok || !data) {
                throw new Error(
                    `Failed to export stek: ${response.status} ${response.statusText}`
                );
            }

            // The data is a Blob because of FileResponse
            const blob = data as Blob;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `stek_export_${stek.value.name}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.add({
                severity: "success",
                summary: "Export Successful",
                detail: `Stek ${stek.value.name} has been exported.`,
                life: 3000,
            });
        } catch (error) {
            console.error("Export failed:", error);
            toast.add({
                severity: "error",
                summary: "Export Failed",
                detail:
                    error instanceof Error ? error.message : "Unknown error",
                life: 5000,
            });
        }
    }

    const fetchStek = async () => {
        const stekId = stekInfo.value?.identity;

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        // If no stek id is set
        if (!stekId) {
            logo.value = undefined;
            description.value = undefined;
            mission.value = undefined;
            vision.value = undefined;
            coreValues.value = undefined;
            specializations.value = undefined;
            targetMarkets.value = undefined;
            stek.value = undefined;
            return;
        }

        // Ensure stek.value is set from steks store before fetching details
        if (!stek.value || stek.value.identity !== stekId) {
            // Wait for selectStekFromSteks to set the stek.value properly
            return;
        }

        try {
            await Promise.all([
                fetchLogo(stekId),
                fetchDescription(stekId),
                fetchMission(stekId),
                fetchVision(stekId),
                fetchCoreValues(stekId),
                fetchSpecializations(stekId),
                fetchTargetMarkets(stekId),
            ]);

            // Update FQDN from stek data, which should already be loaded via steks store
            fqdn.value = stek.value?.fqdn || "";
            originalFqdn.value = fqdn.value;
        } catch (error) {
            console.error("Error fetching Stek details:", error);
            // Still keep the page usable with whatever data we have
            // Logo might be undefined, but that's handled in the template
            throw error;
        }
    };

    const fetchLogo = async (currentStekId?: string | undefined) => {
        if (stek.value) {
            isFetchingLogo.value = true;
            try {
                const logoArtefact = await fetchLogoForStek(stek.value);

                // Check if stek has changed during the async operation
                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during logo fetch, aborting logo update"
                    );
                    return;
                }

                if (logoArtefact) {
                    logo.value = logoArtefact;
                } else {
                    logo.value = Artefact.createEmptyArtefact({
                        mimetype: "image/*",
                        identity: stek.value.identity + "_logo",
                    });
                }

                // Try to create the artefact, but catch errors to prevent breaking the UI
                try {
                    if (!stek.value || stek.value.identity !== currentStekId) {
                        console.debug(
                            "Stek changed during artefact creation, aborting logo update"
                        );
                        return;
                    }
                    logo.value!.links = stek.value.logo?.links;
                } catch (artefactError) {
                    console.error(
                        "Failed to create logo artefact:",
                        artefactError
                    );
                    // If creating the artefact fails, keep using the placeholder
                    // This prevents the UI from breaking if logo processing fails
                }
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Aborted request, this is normal when navigating away
                    return;
                } else {
                    // Log the error but don't throw - let the UI handle fallback
                    console.error("Error fetching logo:", e);
                    // Will use the placeholder logo from the watch handler
                }
            } finally {
                isFetchingLogo.value = false;
            }
        }
    };

    const fetchDescription = async (currentStekId?: string | undefined) => {
        if (!description.value && stek.value) {
            // Store the current stek ID to detect changes during async operation
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    stek.value,
                    "stekDescription",
                    controller.value.signal,
                    revisionQuery.value
                );

                // Check if stek has changed during the async operation
                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingDescription.value = false;
            }
        }
    };

    const fetchMission = async (currentStekId?: string | undefined) => {
        if (!mission.value && stek.value) {
            isFetchingMission.value = true;
            try {
                const missionResult = await getMissionArtefact(
                    stek.value,
                    "stekMission",
                    controller.value.signal,
                    revisionQuery.value
                );

                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during mission fetch, aborting mission update"
                    );
                    return;
                }

                mission.value = missionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingMission.value = false;
            }
        }
    };

    const fetchVision = async (currentStekId?: string | undefined) => {
        if (!vision.value && stek.value) {
            isFetchingVision.value = true;
            try {
                const visionResult = await getVisionArtefact(
                    stek.value,
                    "stekVision",
                    controller.value.signal,
                    revisionQuery.value
                );

                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during vision fetch, aborting vision update"
                    );
                    return;
                }

                vision.value = visionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingVision.value = false;
            }
        }
    };

    const fetchCoreValues = async (currentStekId?: string | undefined) => {
        if (!coreValues.value && stek.value) {
            isFetchingCoreValues.value = true;
            try {
                const coreValuesResult = await getCoreValuesArtefact(
                    stek.value,
                    "stekCoreValues",
                    controller.value.signal,
                    revisionQuery.value
                );

                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during core values fetch, aborting core values update"
                    );
                    return;
                }

                coreValues.value = coreValuesResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingCoreValues.value = false;
            }
        }
    };

    const fetchSpecializations = async (currentStekId?: string | undefined) => {
        if (!specializations.value && stek.value) {
            isFetchingSpecializations.value = true;
            try {
                const specializationsResult = await getSpecializationsArtefact(
                    stek.value,
                    "stekSpecializations",
                    controller.value.signal,
                    revisionQuery.value
                );

                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during specializations fetch, aborting specializations update"
                    );
                    return;
                }

                specializations.value = specializationsResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingSpecializations.value = false;
            }
        }
    };

    const fetchTargetMarkets = async (currentStekId?: string | undefined) => {
        if (!targetMarkets.value && stek.value) {
            isFetchingTargetMarkets.value = true;
            try {
                const targetMarketsResult = await getTargetMarketsArtefact(
                    stek.value,
                    "stekTargetMarkets",
                    controller.value.signal,
                    revisionQuery.value
                );

                if (!stek.value || stek.value.identity !== currentStekId) {
                    console.debug(
                        "Stek changed during target markets fetch, aborting target markets update"
                    );
                    return;
                }

                targetMarkets.value = targetMarketsResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingTargetMarkets.value = false;
            }
        }
    };

    watch(
        () => useReleaseStore().activeRevision,
        () => {
            if (stek.value) {
                // Clear all content so it re-fetches at the new revision
                description.value = undefined;
                mission.value = undefined;
                vision.value = undefined;
                coreValues.value = undefined;
                specializations.value = undefined;
                targetMarkets.value = undefined;
                fetchStek();
            }
        }
    );

    // When the user switches to a different stek, reset release state.
    // Without this, the previously selected release would be applied to the
    // new stek, causing API calls with a non-existent revision and broken rendering.
    watch(
        () => stek.value?.identity,
        (newIdentity, oldIdentity) => {
            if (oldIdentity !== undefined && newIdentity !== oldIdentity) {
                useReleaseStore().resetForNewStek();
            }
        }
    );

    watch(
        () => route.meta.requiresStek,
        (newRequiresStek) => {
            if (!newRequiresStek) {
                stek.value = undefined;
            } else {
                selectStekFromSteks();
            }
        },
        { immediate: true }
    );

    // When the list of steks changes, try to select a stek.
    watch(
        steks,
        () => {
            if (route.meta.requiresStek) {
                selectStekFromSteks();
            }
        },
        { deep: true, immediate: true }
    );

    watch(
        [() => stek.value?.logo?.preview, logo],
        async ([newLogoPreview, newLogo]) => {
            if (newLogo && newLogo.content) {
                logoDataUrl.value = await newLogo.getDataUrl();
            } else if (newLogoPreview) {
                logoDataUrl.value = newLogoPreview;
            } else {
                logoDataUrl.value = "";
            }
        },
        { deep: true }
    );

    // When the stekName in the pathname changes, try to select the new
    // stek using the new stekName.
    watch(
        () => stekInfo.value?.identity,
        (newValue, oldValue) => {
            // Set the header for the WebComponents requests lib.
            headerManager.updateHeaders({
                stek: newValue || "",
            });

            const selectedOrganization = organization.value;
            if (!selectedOrganization) {
                return;
            }

            if (newValue !== oldValue) {
                selectStekFromSteks();
                // No need to call fetchStek() here since selectStekFromSteks() already does it
            }
        },
        { immediate: true }
    );

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    onMounted(() => {
        syncChannel.addEventListener(
            "message",
            async ({ data: { message, identity } }) => {
                if (
                    message === "synced" &&
                    identity.organizationId === organization.value?.identity &&
                    identity.entityType === EntityType.STEK &&
                    identity.entityId === stek.value?.identity
                ) {
                    await fetchSteks();
                    stek.value = steks.value?.find(
                        (_stek) => _stek.identity === stek.value?.identity
                    );
                }
            }
        );
    });

    return {
        logo,
        logoDataUrl,
        fetchLogo,
        fqdn,
        description,
        mission,
        vision,
        coreValues,
        specializations,
        targetMarkets,
        isModified,
        isLoadingSteks,
        stek,
        remove,
        exportStek,
        reset,
        isFetching,
        isStekReady,
    };
});

export const useStekManager = () => storeToRefs(useStekStore());
